import { Box, Stack, Table, TableProps, getDefaultZIndex } from '@mantine/core'
import { ReactNode } from 'react'
import { LoadingOverlay } from '../LoadingOverlay/LoadingOverlay'
import { RenderTableState } from './RenderTableState'
import { useColumnCount } from './useColumnCount'

const TABLE_MIN_WIDTH = 600

type DataTableProps = TableProps & {
  children: ReactNode
  filters?: ReactNode
  hasItems?: boolean
  isLoading?: boolean
  hasError?: boolean
  hasActiveFilter?: boolean
  emptyMessage?: ReactNode
  errorMessage?: ReactNode
  minWidth?: number
  onReload?: () => void
}

export const DataTable = ({
  children,
  filters,
  hasItems,
  isLoading,
  hasError,
  hasActiveFilter,
  emptyMessage,
  errorMessage,
  minWidth = TABLE_MIN_WIDTH,
  onReload,
  ...props
}: DataTableProps) => {
  const { tableRef, columnCount } = useColumnCount()

  return (
    <Stack gap="xs">
      {filters}

      <Box pos="relative">
        {/* Below the modal/drawer layer, otherwise the veil covers dialogs opened while the table loads. */}
        <LoadingOverlay
          visible={isLoading}
          withSpinner={false}
          zIndex={getDefaultZIndex('app')}
        />

        <Table.ScrollContainer minWidth={minWidth}>
          <Table
            ref={tableRef}
            horizontalSpacing="sm"
            verticalSpacing="sm"
            highlightOnHoverColor="gray.1"
            styles={(theme) => ({
              thead: {
                backgroundColor: theme.colors.gray[0]
              }
            })}
            withTableBorder
            highlightOnHover
            {...props}
          >
            {children}

            <Table.Tbody>
              <RenderTableState
                hasItems={hasItems}
                isLoading={isLoading}
                hasError={hasError}
                hasActiveFilter={hasActiveFilter}
                cols={columnCount}
                emptyMessage={emptyMessage}
                errorMessage={errorMessage}
                onReload={onReload}
              />
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Box>
    </Stack>
  )
}
